import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Home, Users, Target, TrendingUp, Shield, Clock, 
  ListTodo, Calculator, Link, MessageSquare, BarChart3,
  RotateCcw, Menu, X, ChevronLeft, ChevronRight
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';

const SidebarNavigation = ({ 
  activeView, 
  setActiveView, 
  hasPermission, 
  currentUser,
  isCollapsed = false,
  onToggleCollapse,
  className = ""
}) => {
  const { t } = useTranslation();
  const [isHovered, setIsHovered] = useState(false);

  const navigationItems = [
    {
      id: 'dashboard',
      label: t('navigation.dashboard'),
      icon: Home,
      permissions: ['Admin', 'Manager', 'Agente', 'CSR', 'Auditor', 'Super Admin'],
      badge: null
    },
    {
      id: 'leads',
      label: t('navigation.leads'),
      icon: Target,
      permissions: ['Admin', 'Manager', 'Agente', 'CSR'],
      badge: null
    },
    {
      id: 'quotes',
      label: t('navigation.quotes'),
      icon: TrendingUp,
      permissions: ['Admin', 'Manager', 'Agente', 'CSR'],
      badge: null
    },
    {
      id: 'policies',
      label: t('navigation.policies'),
      icon: Shield,
      permissions: ['Admin', 'Manager', 'Agente', 'CSR'],
      badge: null
    },
    {
      id: 'users',
      label: t('navigation.users'),
      icon: Users,
      permissions: ['Admin'],
      badge: null
    },
    {
      id: 'renewals',
      label: t('navigation.renewals'),
      icon: RotateCcw,
      permissions: ['Admin', 'Manager', 'Agente', 'CSR'],
      badge: null
    },
    {
      id: 'tasks',
      label: t('navigation.tasks'),
      icon: ListTodo,
      permissions: ['Admin', 'Manager', 'Agente', 'CSR'],
      badge: null
    },
    {
      id: 'commissions',
      label: t('navigation.commissions'),
      icon: Calculator,
      permissions: ['Admin', 'Manager', 'Agente'],
      badge: null
    },
    {
      id: 'integrations',
      label: t('navigation.integrations'),
      icon: Link,
      permissions: ['Admin', 'Manager'],
      badge: null
    },
    {
      id: 'communications',
      label: t('navigation.communications'),
      icon: MessageSquare,
      permissions: ['Admin', 'Manager', 'Agente', 'CSR'],
      badge: null
    },
    {
      id: 'analytics',
      label: t('navigation.analytics'),
      icon: BarChart3,
      permissions: ['Admin', 'Manager'],
      badge: null
    },
    {
      id: 'superadmin',
      label: t('navigation.superAdmin'),
      icon: Shield,
      permissions: ['Super Admin'],
      badge: 'SUPER',
      special: true
    }
  ];

  const visibleItems = navigationItems.filter(item => 
    hasPermission(item.permissions)
  );

  const shouldShowExpanded = !isCollapsed || isHovered;

  return (
    <div 
      className={`bg-white border-r border-gray-200 transition-all duration-300 ${
        shouldShowExpanded ? 'w-64' : 'w-16'
      } ${className}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Sidebar Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          {shouldShowExpanded ? (
            <div className="flex items-center">
              <div className="bg-mg-green text-white p-2 rounded-lg mr-3">
                <div className="text-xs font-bold">CIP</div>
              </div>
              <div>
                <h2 className="text-sm font-semibold text-gray-900">
                  CRM INS PRO
                </h2>
                <p className="text-xs text-gray-500">Insurance System</p>
              </div>
            </div>
          ) : (
            <div className="bg-mg-green text-white p-2 rounded-lg">
              <div className="text-xs font-bold">CIP</div>
            </div>
          )}
          
          {onToggleCollapse && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onToggleCollapse}
              className="p-1 hover:bg-gray-100"
            >
              {shouldShowExpanded ? (
                <ChevronLeft className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </Button>
          )}
        </div>
      </div>

      {/* User Info */}
      {shouldShowExpanded && currentUser && (
        <div className="p-4 border-b border-gray-100">
          <div className="flex items-center">
            <div className="bg-mg-green text-white p-2 rounded-full mr-3 text-xs font-semibold">
              {currentUser.full_name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">
                {currentUser.full_name || currentUser.username}
              </p>
              <p className="text-xs text-gray-500">{currentUser.role}</p>
            </div>
          </div>
        </div>
      )}

      {/* Navigation Items */}
      <nav className="flex-1 p-2">
        <div className="space-y-1">
          {visibleItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeView === item.id;
            
            return (
              <button
                key={item.id}
                onClick={() => setActiveView(item.id)}
                style={{ pointerEvents: 'auto', cursor: 'pointer' }}
                className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  isActive
                    ? 'bg-mg-green text-white'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-mg-green'
                } ${shouldShowExpanded ? 'justify-start' : 'justify-center'}`}
                title={!shouldShowExpanded ? item.label : undefined}
              >
                <Icon 
                  className={`${shouldShowExpanded ? 'mr-3' : ''} ${
                    item.special ? 'text-yellow-400' : ''
                  } h-5 w-5`} 
                  style={{ pointerEvents: 'none' }}
                />
                {shouldShowExpanded && (
                  <>
                    <span className="flex-1 text-left" style={{ pointerEvents: 'none' }}>{item.label}</span>
                    {item.badge && (
                      <Badge 
                        variant="secondary" 
                        className={`text-xs ${
                          item.special ? 'bg-yellow-100 text-yellow-800' : ''
                        }`}
                        style={{ pointerEvents: 'none' }}
                      >
                        {item.badge}
                      </Badge>
                    )}
                  </>
                )}
              </button>
            );
          })}
        </div>

        {/* Separator for Super Admin */}
        {hasPermission(['Super Admin']) && shouldShowExpanded && (
          <>
            <Separator className="my-4" />
            <div className="px-3 py-2">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                {t('navigation.systemControl')}
              </p>
            </div>
          </>
        )}
      </nav>

      {/* Footer */}
      {shouldShowExpanded && (
        <div className="p-4 border-t border-gray-100">
          <div className="text-center">
            <p className="text-xs text-gray-400">
              © 2025 CRM INS PRO
            </p>
            <p className="text-xs text-gray-400">
              by Budget Digital Solutions
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default SidebarNavigation;